import { Helmet } from "react-helmet";
import { useLanguage } from "@/context/LanguageContext";
import { useLocation } from "react-router-dom";
import { useMemo } from "react";

interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string[];
    image?: string;
    imageAlt?: string;
    url?: string;
    type?: 'website' | 'article' | 'software';
    publishedTime?: string;
    dateModified?: string;
    author?: string;
    noindex?: boolean;
    breadcrumbs?: Array<{ name: string; item: string }>;
    language?: 'en' | 'de';
    disableLanguageParam?: boolean;
    applicationCategory?: string;
    operatingSystem?: string;
    price?: string;
    currency?: string;
    aggregateRating?: {
        ratingValue: string;
        reviewCount: string;
    };
}

// Security: Escapes potentially unsafe characters in JSON-LD data to prevent XSS
// when injected into <script> tags. Replaces < with \u003c.
const safeJsonLdStringify = (data: unknown) => {
    return JSON.stringify(data).replace(/</g, '\\u003c');
};

// Default Person Schema (migrated from index.html)
// Moved outside component to avoid recreation on every render
const PERSON_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Kevin Rassner",
    "givenName": "Kevin",
    "familyName": "Rassner",
    "jobTitle": "Systemic Organizational Developer",
    "description": "Systemic Organizational Developer and Strategic Consultant based in Heilbronn, specializing in transforming complex organizations, coaching leadership teams, and scaling startups.",
    "url": "https://www.rassner-dev.org",
    "image": [
        "https://www.rassner-dev.org/Preview-website.png"
    ],
    "address": {
        "@type": "PostalAddress",
        "addressLocality": "Heilbronn",
        "addressCountry": "DE"
    },
    // SEO: Added missing target expertise keywords (Agile, COO, Operations, Operational Excellence)
    // to the Person schema to strengthen E-E-A-T relevance for local Heilbronn searches [Google guideline]
    "knowsAbout": [
        "Organizational Development",
        "Agile",
        "COO",
        "Operations",
        "Operational Excellence",
        "Systemic Consulting",
        "Leadership Coaching",
        "Change Management",
        "AI Implementation"
    ],
    "sameAs": [
        "https://www.linkedin.com/in/kevin-rassner/",
        "https://github.com/kevske"
    ]
};

const PERSON_SCHEMA_JSON = safeJsonLdStringify(PERSON_SCHEMA);

// Constant description for the ProfessionalService schema to ensure
// it represents the business entity consistently across all pages,
// regardless of the individual page description.
const BUSINESS_DESCRIPTION = PERSON_SCHEMA.description;

const PROFESSIONAL_SERVICE_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Kevin Rassner | Systemic Organizational Development",
    "description": BUSINESS_DESCRIPTION,
    "image": "https://www.rassner-dev.org/Preview-website.png",
    "@id": "https://www.rassner-dev.org",
    "url": "https://www.rassner-dev.org/",
    "priceRange": "$$",
    "address": {
        "@type": "PostalAddress",
        "addressLocality": "Heilbronn",
        "addressCountry": "DE"
    },
    "geo": {
        "@type": "GeoCoordinates",
        "latitude": 49.1427,
        "longitude": 9.2109
    },
    "areaServed": [
        "Heilbronn",
        "Neuenstadt",
        "Neckarsulm",
        "Öhringen"
    ],
    "sameAs": [
        "https://www.linkedin.com/in/kevin-rassner/",
        "https://github.com/kevske"
    ],
    "founder": {
        "@type": "Person",
        "name": "Kevin Rassner"
    }
};

const PROFESSIONAL_SERVICE_SCHEMA_JSON = safeJsonLdStringify(PROFESSIONAL_SERVICE_SCHEMA);

const SEO = ({
    title = "Kevin Rassner | Systemic Org. Development",
    // SEO: Shortened description to < 160 characters
    description = "Expert in Organizational Development & Agile transformation in Heilbronn. Systemic consulting for COOs and scaling startups.",
    keywords = ["Organizational Development", "Agile", "COO", "Operations", "Operational Excellence", "Heilbronn", "Neuenstadt", "Neckarsulm", "Öhringen", "Systemic Consulting"],
    image = "https://www.rassner-dev.org/Preview-website.png",
    imageAlt = "Kevin Rassner - Systemic Organizational Developer",
    url,
    type = "website",
    publishedTime,
    dateModified,
    author = "Kevin Rassner",
    noindex = false,
    breadcrumbs,
    language: propLanguage,
    disableLanguageParam = false,
    applicationCategory,
    operatingSystem,
    price,
    currency,
    aggregateRating
}: SEOProps) => {
    const { language: contextLanguage } = useLanguage();
    // Use prop language if provided (e.g. for blog posts), otherwise fallback to context
    const language = propLanguage || contextLanguage;
    const location = useLocation();
    const siteTitle = title === "Kevin Rassner | Systemic Org. Development" ? title : `${title} | Kevin Rassner`;

    // Dynamic canonical URL generation
    // Base URL is always the clean URL without query parameters
    const baseUrl = url ? url.replace(/\/$/, "") : `https://www.rassner-dev.org${location.pathname}`.replace(/\/$/, "");

    // Canonical URL:
    // If we are in German mode, the canonical URL for THIS page should include ?lang=de
    // explicitly telling Google "this is the German version".
    // unless disableLanguageParam is true (e.g. for blog posts which satisfy language via content)
    // If we are in English (default), it's just the base URL.
    const canonicalUrl = (language === 'de' && !disableLanguageParam) ? `${baseUrl}?lang=de` : baseUrl;

    // Generate Hreflang URLs
    // English is default, so it maps to the clean base URL
    const enUrl = baseUrl;
    // German is explicit
    const deUrl = disableLanguageParam ? baseUrl : `${baseUrl}?lang=de`;

    // SEO: Added WebSite schema for the homepage to define the site name and structure.
    // This helps Google understand the site identity and potential search capabilities.
    const webSiteSchemaJson = useMemo(() => {
        if (location.pathname !== '/') return null;
        const schema = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Kevin Rassner | Systemic Organizational Development",
            "url": "https://www.rassner-dev.org/",
            "author": {
                "@type": "Person",
                "name": author
            }
        };
        return safeJsonLdStringify(schema);
    }, [location.pathname, author]);

    const articleSchemaJson = useMemo(() => {
        if (type !== 'article') return null;
        const schema = {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": title,
            "description": description,
            "image": image ? [image] : [],
            "datePublished": publishedTime,
            "dateModified": dateModified || publishedTime,
            "author": {
                "@type": "Person",
                "name": author,
                "url": "https://www.rassner-dev.org"
            },
            "publisher": {
                "@type": "Organization",
                "name": "Kevin Rassner",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://www.rassner-dev.org/favicon/android-chrome-512x512.png"
                }
            },
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": canonicalUrl
            }
        };
        return safeJsonLdStringify(schema);
    }, [type, title, description, image, publishedTime, dateModified, author, canonicalUrl]);

    // SEO: Added SoftwareApplication schema for tools/apps
    const softwareApplicationSchemaJson = useMemo(() => {
        if (type !== 'software') return null;
        const schema = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": siteTitle,
            "description": description,
            "image": image,
            "url": canonicalUrl,
            "applicationCategory": applicationCategory || "BusinessApplication",
            "operatingSystem": operatingSystem || "Web",
            "offers": {
                "@type": "Offer",
                "price": price || "0",
                "priceCurrency": currency || "EUR"
            },
            ...(aggregateRating ? {
                "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": aggregateRating.ratingValue,
                    "reviewCount": aggregateRating.reviewCount
                }
            } : {}),
            "author": {
                "@type": "Person",
                "name": author
            }
        };
        return safeJsonLdStringify(schema);
    }, [type, siteTitle, description, image, canonicalUrl, applicationCategory, operatingSystem, price, currency, aggregateRating, author]);

    // SEO: Added WebPage schema for non-article/non-software pages to explicitly link metadata to the URL
    // This improves understanding of the page context distinct from the global ProfessionalService
    const webPageSchemaJson = useMemo(() => {
        if (type === 'article' || type === 'software') return null;

        const schema = {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": siteTitle,
            "description": description,
            "url": canonicalUrl,
            "isPartOf": {
                "@type": "Website",
                "name": "Kevin Rassner | Systemic Organizational Development",
                "url": "https://www.rassner-dev.org/"
            }
        };
        return safeJsonLdStringify(schema);
    }, [type, siteTitle, description, canonicalUrl]);

    // Generate BreadcrumbList
    const breadcrumbSchemaJson = useMemo(() => {
        let schema = null;
        if (breadcrumbs && breadcrumbs.length > 0) {
            schema = {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": breadcrumbs.map((crumb, index) => ({
                    "@type": "ListItem",
                    "position": index + 1,
                    "name": crumb.name,
                    "item": crumb.item
                }))
            };
        } else if (location.pathname !== "/") {
            // Fallback: Home -> Current Page
            schema = {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://www.rassner-dev.org/"
                    },
                    {
                        "@type": "ListItem",
                        "position": 2,
                        "name": siteTitle,
                        "item": canonicalUrl
                    }
                ]
            };
        }
        return schema ? safeJsonLdStringify(schema) : null;
    }, [breadcrumbs, location.pathname, siteTitle, canonicalUrl]);

    return (
        <Helmet>
            <html lang={language} />
            <title>{siteTitle}</title>
            <meta name="description" content={description} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            {keywords.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={siteTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:alt" content={imageAlt} />
            <meta property="og:locale" content={language === 'de' ? 'de_DE' : 'en_US'} />
            <meta property="og:site_name" content="Kevin Rassner | Systemic Organizational Development" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={siteTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:image:alt" content={imageAlt} />
            <meta name="twitter:creator" content="@kevske" />

            {/* Canonical URL */}
            <link rel="canonical" href={canonicalUrl} />

            {/* Hreflang Tags - Using independent boolean checks to avoid Fragments for react-helmet compatibility */}
            {disableLanguageParam && (
                <link rel="alternate" hrefLang={language === 'de' ? 'de' : 'en'} href={baseUrl} />
            )}

            {!disableLanguageParam && (
                <link rel="alternate" hrefLang="en" href={enUrl} />
            )}
            {!disableLanguageParam && (
                <link rel="alternate" hrefLang="de" href={deUrl} />
            )}
            {!disableLanguageParam && (
                <link rel="alternate" hrefLang="x-default" href={enUrl} />
            )}

            {/* Structured Data */}
            <script type="application/ld+json" suppressHydrationWarning>
                {PERSON_SCHEMA_JSON}
            </script>
            <script type="application/ld+json" suppressHydrationWarning>
                {PROFESSIONAL_SERVICE_SCHEMA_JSON}
            </script>
            {webSiteSchemaJson && (
                <script type="application/ld+json" suppressHydrationWarning>
                    {webSiteSchemaJson}
                </script>
            )}
            {articleSchemaJson && (
                <script type="application/ld+json" suppressHydrationWarning>
                    {articleSchemaJson}
                </script>
            )}
            {softwareApplicationSchemaJson && (
                <script type="application/ld+json" suppressHydrationWarning>
                    {softwareApplicationSchemaJson}
                </script>
            )}
            {webPageSchemaJson && (
                <script type="application/ld+json" suppressHydrationWarning>
                    {webPageSchemaJson}
                </script>
            )}
            {breadcrumbSchemaJson && (
                <script type="application/ld+json" suppressHydrationWarning>
                    {breadcrumbSchemaJson}
                </script>
            )}
        </Helmet>
    );
};

export default SEO;